import React, { useState } from 'react';
import { ChefHat, ShoppingBag, Flame, Leaf } from 'lucide-react';
import { FEATURED, RECIPES } from '../mock/mock';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

export default function FeaturedSpice() {
  const [expanded, setExpanded] = useState(false);
  const [size, setSize] = useState(FEATURED.sizes[1]);
  const { addItem } = useCart();
  const { toast } = useToast();

  const handleAdd = () => {
    addItem({ id: FEATURED.id, name: FEATURED.title, image: FEATURED.image, price: FEATURED.price }, size);
    toast({ title: 'Added to cart', description: `${FEATURED.title} · ${size}` });
  };

  return (
    <section id="blends" style={{ background: 'var(--gk-cream-dark)' }} className="py-16 md:py-24 grain-bg">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <div className="divider-leaf max-w-md mx-auto mb-4 text-xs tracking-[0.3em] uppercase font-semibold">• Featured Blend of the Month •</div>
          <h2 className="section-title text-3xl md:text-4xl">Our Featured Spice of the Month</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <h3 className="font-serif text-4xl md:text-5xl font-bold" style={{ color: 'var(--gk-green-dark)' }}>{FEATURED.title}</h3>
            <p className="mt-4 text-lg font-semibold" style={{ color: 'var(--gk-terracotta)' }}>{FEATURED.tagline}</p>

            <div className={`mt-6 space-y-4 text-[15px] leading-relaxed ${expanded ? '' : 'max-h-[190px] overflow-hidden relative'}`} style={{ color: 'var(--gk-ink)' }}>
              {FEATURED.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              {!expanded && (
                <div className="absolute bottom-0 left-0 right-0 h-16" style={{ background: 'linear-gradient(to bottom, transparent, var(--gk-cream-dark))' }} />
              )}
            </div>
            <button onClick={() => setExpanded(v => !v)} className="mt-3 text-sm font-semibold link-underline" style={{ color: 'var(--gk-green-dark)' }}>
              {expanded ? 'Show less ▲' : 'Show more ▼'}
            </button>

            <div className="mt-8 p-6 rounded-xl" style={{ background: '#fff', border: '1px solid #ecdfc7' }}>
              <div className="flex items-center gap-2 mb-4">
                <ChefHat size={18} style={{ color: 'var(--gk-terracotta)' }} />
                <span className="font-serif text-lg font-bold" style={{ color: 'var(--gk-green-dark)' }}>How to use</span>
              </div>
              <div className="space-y-4">
                {FEATURED.usage.map((u, i) => (
                  <div key={i}>
                    <div className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--gk-ink)' }}>
                      <Flame size={13} style={{ color: 'var(--gk-gold-dark)' }} /> {u.title}
                    </div>
                    <p className="text-sm mt-1 pl-5" style={{ color: 'var(--gk-muted)' }}>{u.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="md:sticky md:top-24">
            <div className="relative rounded-2xl overflow-hidden" style={{ boxShadow: '0 30px 60px -30px rgba(63,82,37,0.5)' }}>
              <img src={FEATURED.image} alt={FEATURED.title} className="w-full" />
              <div className="absolute top-4 right-4 chip" style={{ background: 'var(--gk-green-dark)', color: 'var(--gk-gold)' }}>
                <Leaf size={12} /> Small Batch
              </div>
            </div>
            <div className="mt-6 p-6 rounded-xl" style={{ background: '#fff', border: '1px solid #ecdfc7' }}>
              <div className="text-sm uppercase tracking-widest mb-2" style={{ color: 'var(--gk-muted)' }}>Choose size</div>
              <div className="flex flex-col gap-2">
                {FEATURED.sizes.map(s => (
                  <button key={s} onClick={() => setSize(s)} className="text-left px-4 py-2.5 rounded-lg text-sm transition-colors"
                    style={size === s
                      ? { background: 'var(--gk-green)', color: '#fff', border: '1px solid var(--gk-green)' }
                      : { background: '#faf6ee', color: 'var(--gk-ink)', border: '1px solid #ecdfc7' }}>
                    {s}
                  </button>
                ))}
              </div>
              <button onClick={handleAdd} className="gk-btn-primary w-full justify-center mt-4">
                <ShoppingBag size={16} /> Add to Cart
              </button>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <h4 className="font-serif text-xl md:text-2xl font-bold mb-6" style={{ color: 'var(--gk-green-dark)' }}>Try these recipes with Biryani Masala</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {RECIPES.map(r => (
              <a key={r.id} href="#recipes" className="group">
                <div className="aspect-square rounded-lg overflow-hidden mb-2">
                  <img src={r.img} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="text-sm font-semibold leading-snug" style={{ color: 'var(--gk-ink)' }}>{r.title}</div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
